import React, { useState } from 'react';
import { User, Mail, Phone, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import NameInput from './ui/NameInput';
import PhoneInput from './ui/PhoneInput';

const BookingDetailsForm = ({ 
  onSubmit, 
  loading = false, 
  submitButtonText = "Continue",
  property,
  checkIn,
  checkOut,
  guests,
  totalPrice,
  showPropertySummary = true,
  bookingType = 'full_property',
  roomName = null,
  roomNumber = null,
  defaults = null  // { firstName, lastName, email, phone } — pre-fills from logged-in user
}) => {
  // Split a stored full_name back into first/last for the form. If only one
  // word exists, treat it as the first name.
  const splitName = (full) => {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return ['', ''];
    if (parts.length === 1) return [parts[0], ''];
    return [parts[0], parts.slice(1).join(' ')];
  };
  const [defFirst, defLast] = splitName(defaults?.fullName);

  const [formData, setFormData] = useState({
    firstName: defaults?.firstName || defFirst || '',
    lastName: defaults?.lastName || defLast || '',
    email: defaults?.email || '',
    phone: defaults?.phone || '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'Please enter a valid first name';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Please enter a valid last name';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        customerName: `${formData.firstName} ${formData.lastName}`.trim(),
        customerEmail: formData.email,
        customerPhone: formData.phone,
        message: formData.message
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Property Summary */}
      {showPropertySummary && property && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3 text-lg">Booking Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Property:</span>
              <span className="font-medium text-gray-900 text-right">
                {property.title}
                {bookingType === 'single_room' && (roomName || roomNumber != null) && (
                  <> &ndash; {roomName || `Room ${roomNumber}`}</>
                )}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Check-in:</span>
              <span className="font-medium text-gray-900">{formatDate(checkIn)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Check-out:</span>
              <span className="font-medium text-gray-900">{formatDate(checkOut)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Nights:</span>
              <span className="font-medium text-gray-900">{calculateNights()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Guests:</span>
              <span className="font-medium text-gray-900">
                {(guests?.adults || 0) + (guests?.children || 0)} guest{(guests?.adults || 0) + (guests?.children || 0) !== 1 ? 's' : ''}
              </span>
            </div>
            {totalPrice && (
              <div className="flex justify-between text-base font-bold border-t border-blue-300 pt-2 mt-2">
                <span className="text-gray-900">Total:</span>
                <span className="text-blue-600">CA${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : totalPrice}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Guest Information Form */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Guest Information</h3>
        
        {/* First & Last Name */}
        <div className="mb-4">
          <NameInput
            firstName={formData.firstName}
            lastName={formData.lastName}
            onFirstNameChange={(value) => setFormData({ ...formData, firstName: value })}
            onLastNameChange={(value) => setFormData({ ...formData, lastName: value })}
            firstNameError={errors.firstName}
            lastNameError={errors.lastName}
            required
          />
        </div>

        {/* Email */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`block w-full pl-10 pr-3 py-3 border ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-lg shadow-sm focus:ring-2 transition-all`}
              placeholder="john.doe@example.com"
              required
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone with Country Code */}
        <div className="mb-4">
          <PhoneInput
            value={formData.phone}
            onChange={(value) => setFormData({ ...formData, phone: value })}
            error={errors.phone}
            required
          />
        </div>

        {/* Message to Host */}
        <div className="mb-4">
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Message to Host <span className="text-gray-500">(Optional)</span>
          </label>
          <div className="relative">
            <div className="absolute top-3 left-3 pointer-events-none">
              <MessageSquare className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
              placeholder="Let the host know why you're traveling, arrival time, any special requests..."
            ></textarea>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            This message will be sent directly to the host
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
            Processing...
          </div>
        ) : (
          submitButtonText
        )}
      </Button>

      {/* Info Box */}
      <Card className="p-4 bg-gray-50 border border-gray-200">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> By clicking "{submitButtonText}", you agree to our{' '}
          <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> and{' '}
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
        </p>
      </Card>
    </form>
  );
};

export default BookingDetailsForm;
